/* eslint-disable react/jsx-no-bind */
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { connect } from 'react-redux';
import { bindActionCreators, compose } from 'redux';
import PropTypes from 'prop-types';
import { useNavigate } from 'react-router-dom';
import { getCountryOptions, useAddressValidationFlow } from '@homeexchange/home';
import { buildAddressText } from '../../../utils/buildAddressText';

import SignupCollectionAction from '../SignupCollectionAction';
import HomeAddressCollection from '../HomeAddressCollection';
import { setFormData } from '../../../actions/collection';
import HomeApi from '../../../api/Home';
import i18n from '../../../i18n';
import withAnalytics from '../../analytics/withAnalytics';
import CollectionFunnelEvents from '../../../utils/collectionFunnelEvents';

export const Step3Collection = (props) => {
    const [isSending, setIsSending] = useState(false);
    const navigate = useNavigate();

    const locale = i18n.language;
    const sessionToken = useMemo(() => crypto.randomUUID(), []);
    const countryOptions = useMemo(() => getCountryOptions(locale), [locale]);
    const wasManualRef = useRef(false);

    const config = useMemo(
        () => ({
            theme: 'collection',
            locale,
            googleMapsApiKey: process.env.GOOGLE_MAPS_API_KEY,
            googleMapsMapId: process.env.GOOGLE_MAPS_MAP_ID,
            sessionToken
        }),
        [locale, sessionToken]
    );

    const initialValue = useMemo(() => {
        const address = props.formCollectionData?.address;
        if (!address) {
            return undefined;
        }
        return {
            address,
            location: props.formCollectionData?.location
        };
    }, [props.formCollectionData]);

    const handleAccepted = async (value) => {
        setIsSending(true);
        const payload = {
            ...props.formCollectionData,
            address: buildAddressText(value, countryOptions),
            location: value.location,
            location_id: value.location_id,
            overridden_address: wasManualRef.current ? 1 : 0
        };
        delete payload.provider;

        try {
            await HomeApi.addHomeCollection(payload);
            props.setFormData(payload);

            CollectionFunnelEvents.funnelProgress(3);
            props.track('HEC Home init', { category: 'HEC application' });
            navigate(location.pathname.replace(/[^/]*$/, 'step4'));
        } catch (error) {
            setIsSending(false);
            console.error(error);
        }
    };

    const addressFlow = useAddressValidationFlow({
        onAccepted: handleAccepted,
        initialValue,
        locale
    });

    useEffect(() => {
        if (addressFlow.step === 'manual') {
            wasManualRef.current = true;
        }
    }, [addressFlow.step]);

    useEffect(() => {
        if (_.isEmpty(props.formCollectionData)) {
            HomeApi.getHomeCollection().then((response) => {
                if (response) {
                    props.setFormData(response);
                }
            });
        }
    }, []);

    const handleBackOverride = () => {
        if (addressFlow.step === 'confirm' || addressFlow.step === 'manual' || addressFlow.step === 'pin') {
            addressFlow.onBack();
            return true;
        }
        return false;
    };

    const isAddressActionEnabled =
        !addressFlow.isConfirmDisabled && (addressFlow.step === 'manual' || !!addressFlow.value?.address);

    return (
        <>
            <div className="step3-container">
                <div className="step-circle">
                    <span>3</span>
                </div>
                <HomeAddressCollection
                    addressFlow={addressFlow}
                    config={config}
                    countryOptions={countryOptions}
                    onConfirm={addressFlow.onConfirm}
                />
            </div>
            <SignupCollectionAction
                step={3}
                nextLabel={i18n.t(
                    addressFlow.step === 'address' ? 'global:global.continue' : 'global:global.confirm'
                )}
                isValid={isAddressActionEnabled}
                isSending={isSending || addressFlow.isValidating}
                onBackOverride={handleBackOverride}
            />
        </>
    );
};

Step3Collection.propTypes = {
    formCollectionData: PropTypes.object,
    setFormData: PropTypes.func,
    track: PropTypes.func
};

const mapDispatchToProps = (dispatch) => ({
    setFormData: bindActionCreators(setFormData, dispatch)
});

const mapStateToProps = (state) => ({
    formCollectionData: state.collection.formCollection
});

export default compose(connect(mapStateToProps, mapDispatchToProps), withAnalytics)(Step3Collection);
